import { $actionEvent } from '@/actions/actionEvent';
import { type Shortcuts, tryShortcut } from '@/actions/keyboardActions';
import { moveHighlight } from '@/actions/utils';
import { type RenderedCell, cellStyleToCss } from '@/model/cellStyle';
import {
  RichText,
  RichTextRun,
  type RichTextStyle,
  richText,
  richTextToHtml,
} from '@/model/richText';
import type { Sheet } from '@/model/sheet';
import { type StyleToMerge, mergeStyles } from '@/model/types';
import { $selectedRichTextStyle } from '@/utils/selectUtils';
import { Vector2 } from '@/utils/vec';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import { useEffect } from 'react';

export type RichTextSelection = {
  /** Relative to start run */
  start: number;
  /** Relative to end run */
  end: number;
  /** Selected runs */
  runs: RichTextRun[];
};

export const $richText = atom<RichText | null>(null);
export const $cell = atom<RenderedCell | null>(null);
export const $pos = atom<Vector2 | null>(null);
export const $size = atom<Vector2 | null>(null);
export const $element = atom<HTMLElement | null>(null);
export const $selection = atom<RichTextSelection | null>(null);
export const $cb = atom<(value: RichText | string) => void>(() => {});

export function editCellPos(cellPos: Vector2, sheet: Sheet) {
  const cell = sheet.getCellAtOrDefault(cellPos);
  startEditing(
    sheet.getRenderedCellAt(cellPos),
    sheet.getCellPos(cellPos),
    sheet.getCellSize(cellPos),
    (value: RichText | string) => {
      const tx = sheet.startTransaction();
      sheet.setCell(cell.withValue(value));
      tx.commit(`Edit cell ${cellPos.toString()}`);
    },
  );
}

export function startEditing(
  cell: RenderedCell,
  pos: Vector2,
  size: Vector2,
  cb: (value: RichText | string) => void,
) {
  $richText.set(richText(cell.value).flatten());
  $cell.set(cell);
  $pos.set(pos);
  $size.set(size);
  $cb.set(cb);
}

export function finishEditing() {
  const cb = $cb.get();
  const rt = $richText.get();
  if (rt) {
    cb(rt.flatten());
  }
  $selectedRichTextStyle.set({});
  $richText.set(null);
  $cell.set(null);
  $pos.set(null);
  $size.set(null);
  $cb.set(() => {});
}

function findElement(node: Node): HTMLElement {
  while (node.nodeType !== Node.ELEMENT_NODE || !(node as HTMLElement).id) {
    node = node.parentNode!;
  }
  return node as HTMLElement;
}

function setSelection(sel: RichTextSelection | null, dontUpdate = false) {
  $selection.set(sel);
  $selectedRichTextStyle.set(sel?.runs[0]?.style || {});
  if (!sel || dontUpdate) return;

  const startEl = document.getElementById(sel.runs[0]!.id)!;
  const endEl = document.getElementById(sel.runs[sel.runs.length - 1]!.id)!;
  const range = document.createRange();
  range.setStart(startEl.firstChild as Text, sel.start);
  range.setEnd(endEl.firstChild as Text, sel.end);
  const sel2 = window.getSelection();
  if (sel2) {
    sel2.removeAllRanges();
    sel2.addRange(range);
  }
}

function replaceRunsInElement(ogs: RichTextRun[], news: RichTextRun[]) {
  const html = richTextToHtml(
    new RichText().withRuns(news),
    $cell.get()!.style,
  );

  const firstElement = document.getElementById(ogs[0]!.id)!;

  for (let i = 1; i < ogs.length; i++) {
    const el = document.getElementById(ogs[i]!.id)!;
    el.remove();
  }

  firstElement.replaceWith(html);
}

export function applyStyle(style: StyleToMerge<RichTextStyle>) {
  const rt = $richText.get();
  if (!rt) return;
  const sel = $selection.get();
  if (!sel) return;

  if (sel.runs.length === 1 && sel.start === sel.end) {
    const split = sel.runs[0]!.split(sel.start);
    const newRun = new RichTextRun('', mergeStyles(split[0]!.style, style));
    const newRuns = [split[0], newRun, split[1]].filter(
      Boolean,
    ) as RichTextRun[];
    rt.replaceWith(sel.runs, newRuns);
    replaceRunsInElement(sel.runs, newRuns);
    setSelection({
      start: 0,
      end: 0,
      runs: [newRun],
    });
    return;
  }

  const firstSplit = sel.runs[0]!.split(sel.start);
  const lastSplit = (() => {
    if (sel.runs.length === 1) {
      if (!firstSplit[1]) return [null, null];
      const splits = firstSplit[1]!.split(
        sel.end - (firstSplit[0]?.text.length ?? 0),
      );
      firstSplit[1] = splits[0];
      return [null, splits[1]];
    }
    return sel.runs[sel.runs.length - 1]!.split(sel.end);
  })();
  const middleRuns = sel.runs.slice(1, sel.runs.length - 1);

  const firstRun = firstSplit[0];
  const lastRun = lastSplit[1];
  const newMiddleRuns = [firstSplit[1], ...middleRuns, lastSplit[0]].filter(
    Boolean,
  ) as RichTextRun[];

  newMiddleRuns.forEach(run => {
    run.withStyle(style);
  });

  const newRuns = [firstRun, ...newMiddleRuns, lastRun].filter(
    Boolean,
  ) as RichTextRun[];
  rt.replaceWith(sel.runs, newRuns);
  replaceRunsInElement(sel.runs, newRuns);
  setSelection({
    start: 0,
    end: newMiddleRuns[newMiddleRuns.length - 1]!.text.length,
    runs: newMiddleRuns,
  });
}

export const editShortcuts: Shortcuts = {
  Tab: () => (moveHighlight(Vector2.right), finishEditing()),
  ShiftTab: () => (moveHighlight(Vector2.left), finishEditing()),
  // Enter: () => (moveHighlight(Vector2.down), finishEditing()),
  ShiftEnter: () => (moveHighlight(Vector2.up), finishEditing()),
  Escape: () => finishEditing(),
};

export function RichTextEditor() {
  const richText = useStore($richText);
  const pos = useStore($pos);
  const size = useStore($size);
  const cell = useStore($cell);
  const div = useStore($element);

  useEffect(() => {
    if (!div || richText === null || !cell) return;
    div.focus();
    // eslint-disable-next-line react-hooks/immutability
    div.innerHTML = '';
    div.append(richTextToHtml(richText, cell.style));

    // Place cursor at the end
    const last = richText.getRuns()[richText.getRuns().length - 1];
    setSelection({
      start: last!.text.length,
      end: last!.text.length,
      runs: [last!],
    });

    function onSelectionChange() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || !richText) return;

      const range = sel.getRangeAt(0);
      const el = $element.get();
      if (!el || !el.contains(range.commonAncestorContainer)) return;

      const startEl = findElement(range.startContainer);
      const endEl = findElement(range.endContainer);

      const startRun = richText.byId(startEl.id);
      const endRun = richText.byId(endEl.id);

      if (!startRun || !endRun) {
        return;
      }

      const start = range.startOffset;
      const end = range.endOffset;

      setSelection(
        {
          start,
          end,
          runs: richText
            .getRuns()
            .slice(richText.getIndex(startRun), richText.getIndex(endRun) + 1),
        },
        true,
      );
    }

    const off = $actionEvent.ons({
      cellStyle: style => {
        applyStyle(style);
      },
    });

    function onBeforeInput(e: InputEvent) {
      const currSel = $selection.get();

      if (e.inputType === 'historyUndo' || e.inputType === 'historyRedo') {
        // TODO: implement undo/redo history
        e.preventDefault();
        return;
      }

      if (
        currSel &&
        currSel.start === currSel.end &&
        currSel.runs.length === 1 &&
        currSel.runs[0]!.text.length === 0
      ) {
        if (
          e.inputType === 'insertText' ||
          e.inputType === 'insertLineBreak' ||
          e.inputType === 'insertParagraph' ||
          e.inputType === 'insertFromPaste' ||
          e.inputType === 'insertFromDrop'
        ) {
          e.preventDefault();
          const run = currSel.runs[0]!;
          run.withText(run.text + e.data);
          const el = document.getElementById(run.id);
          if (el) {
            run.updateHtml(el, cell!.style);
          } else {
            const newEl = run.toHtml(cell!.style);
            div!.appendChild(newEl);
          }

          setSelection({
            start: run.text.length,
            end: run.text.length,
            runs: [run],
          });
          return;
        }
      }
    }

    document.addEventListener('selectionchange', onSelectionChange);
    div.addEventListener('beforeinput', onBeforeInput);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
      div.removeEventListener('beforeinput', onBeforeInput);
      off();
    };
  }, [cell, div, richText]);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    e.stopPropagation();
    tryShortcut(e, editShortcuts);
  }

  function onInput(e: React.InputEvent<HTMLDivElement>) {
    // Wrap any trailing text in the closest run
    const el = $element.get();
    const rt = $richText.get();
    if (!el || !rt) return;

    const nodes = Array.from(el.childNodes);

    out: if (nodes.length === 1 || nodes.length === 2) {
      if (
        nodes[0]!.nodeType === Node.ELEMENT_NODE &&
        (nodes[0] as HTMLElement).id
      ) {
        break out;
      }

      const text = nodes[0]!.textContent ?? '';
      rt.withRuns([rt.getRuns()[0]!.withText(text)]);
      el.innerHTML = '';
      el.append(richTextToHtml(rt, cell!.style));

      setSelection({
        start: rt.getRuns()[0]!.text.length,
        end: rt.getRuns()[0]!.text.length,
        runs: [rt.getRuns()[0]!],
      });
      return;
    }

    rt.updateFromHtml(el);
  }

  if (richText === null || pos === null || size === null || cell === null)
    return null;

  return (
    <div
      className="absolute after:pointer-events-none after:absolute after:inset-0 after:border-3 after:border-green-800"
      style={{
        background: 'white',
        top: pos.y,
        left: pos.x,
        minWidth: size.x,
        minHeight: size.y,
      }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      <div
        ref={s => {
          if ($element.get() !== s) $element.set(s);
        }}
        className="text-nowrap outline-none [font-synthesis:none]"
        style={{
          ...cellStyleToCss(cell.style),
          fontSize: 0, // MUST have this otherwise the browser sets a "minimum" line height
        }}
        contentEditable="plaintext-only"
        suppressContentEditableWarning
        onKeyDown={onKeyDown}
        onInput={onInput}
        // Can't put onBeforeInput here since React doesn't actually support it
        // See: https://github.com/react/react/issues/11211
      ></div>
    </div>
  );
}
